import React, { useEffect, useRef } from 'react';

// Plancher d’opacité : l’ivoire plein à 0.55 sur --ink-950 donne un contraste
// d’environ 6:1, soit au-dessus du seuil AA pour du texte courant.
const OPACITE_MIN = 0.55;

/**
 * Éclaire le texte mot à mot au fil du défilement : ce qui a été lu reste
 * lumineux, ce qui vient est en retrait.
 *
 * La position de chaque mot est mesurée une fois puis mise en cache ; seul le
 * bloc est mesuré à chaque image. Lire la position de 120 mots à chaque frame
 * forcerait autant de recalculs de mise en page.
 *
 * Le plancher d'opacité est volontairement haut : un mot pas encore « lu » doit
 * rester lisible, et contrasté, pour qui ne fait pas défiler la page.
 */
const RevealText = ({ children, className = '' }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const mots = Array.from(el.querySelectorAll('.rt-mot'));
    if (!mots.length) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      mots.forEach((m) => {
        m.style.opacity = '1';
      });
      return undefined;
    }

    // Chaque mot reçoit une position de lecture : sa ligne, plus un décalage
    // proportionnel à son avancement sur cette ligne. Le balayage suit donc
    // l'œil de gauche à droite au lieu d'allumer la ligne entière d'un bloc.
    let offsets = [];
    const mesurer = () => {
      const bloc = el.getBoundingClientRect();
      const largeur = bloc.width || 1;
      const ligne = parseFloat(getComputedStyle(el).lineHeight) || 24;
      offsets = mots.map((m) => {
        const r = m.getBoundingClientRect();
        return r.top - bloc.top + ((r.left - bloc.left) / largeur) * ligne;
      });
    };

    let frame = 0;
    const peindre = () => {
      frame = 0;
      const haut = el.getBoundingClientRect().top;
      const vh = window.innerHeight;
      const depart = vh * 0.82;
      const arrivee = vh * 0.38;
      mots.forEach((m, i) => {
        const y = haut + offsets[i];
        const t = (depart - y) / (depart - arrivee);
        const v = Math.min(1, Math.max(OPACITE_MIN, t));
        m.style.opacity = String(Math.round(v * 100) / 100);
      });
    };

    const auDefilement = () => {
      if (!frame) frame = requestAnimationFrame(peindre);
    };

    const auRedimensionnement = () => {
      mesurer();
      auDefilement();
    };

    mesurer();
    peindre();
    window.addEventListener('scroll', auDefilement, { passive: true });
    window.addEventListener('resize', auRedimensionnement);
    document.fonts?.ready.then(auRedimensionnement);

    return () => {
      window.removeEventListener('scroll', auDefilement);
      window.removeEventListener('resize', auRedimensionnement);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [children]);

  // Le texte est découpé en mots mais reste dans l'ordre du document :
  // un lecteur d'écran le restitue normalement.
  const decouper = (noeud, cle) => {
    if (typeof noeud === 'string') {
      return noeud.split(/(\s+)/).map((bout, i) =>
        /^\s+$/.test(bout) ? (
          bout
        ) : (
          <span className="rt-mot" key={`${cle}-${i}`}>
            {bout}
          </span>
        )
      );
    }
    if (React.isValidElement(noeud)) {
      return React.cloneElement(noeud, {
        children: React.Children.map(noeud.props.children, (enfant, i) =>
          decouper(enfant, `${cle}-${i}`)
        )
      });
    }
    return noeud;
  };

  return (
    <p className={`rt ${className}`} ref={ref}>
      {React.Children.map(children, (enfant, i) => decouper(enfant, i))}
    </p>
  );
};

export default RevealText;
